import { useState, useEffect } from 'react';
import { Shuffle } from 'lucide-react';
import { valuesData } from './data';

interface ValuesSelectionProps {
  selectedValues: string[];
  onToggleValue: (abbr: string) => void;
  onContinue: () => void;
}

export function ValuesSelection({ selectedValues, onToggleValue, onContinue }: ValuesSelectionProps) {
  const [displayedValues, setDisplayedValues] = useState<string[]>([]);

  // Get all value abbreviations as a flat list
  const getAllValueAbbrs = () => {
    return valuesData.flatMap(cluster => cluster.motives.map(m => m.abbr));
  };

  // Helper function to get a random set of values
  const getRandomValues = (count: number, mustInclude: string[] = []) => {
    const allValues = getAllValueAbbrs();
    const availableValues = allValues.filter(v => !mustInclude.includes(v));

    const shuffled = [...availableValues].sort(() => Math.random() - 0.5);
    const needed = Math.max(0, count - mustInclude.length);
    const randomPicks = shuffled.slice(0, needed);

    return [...mustInclude, ...randomPicks];
  };

  // Initialize displayed values
  useEffect(() => {
    const displayed = getRandomValues(15, selectedValues);
    setDisplayedValues(displayed);
  }, []);

  // Regenerate displayed values while keeping selected ones
  const regenerateDisplayedValues = () => {
    const displayed = getRandomValues(15, selectedValues);
    setDisplayedValues(displayed);
  };

  // Get value description from abbreviation
  const getValueDescription = (abbr: string) => {
    for (const cluster of valuesData) {
      const motive = cluster.motives.find(m => m.abbr === abbr);
      if (motive) {
        return motive.description;
      }
    }
    return abbr;
  };

  return (
    <div className="tw-space-y-3">
      <div className="tw-space-y-1">
        <h2 className="tw-text-xl tw-font-bold tw-text-gray-800">What matters most to you?</h2>
        <p className="tw-text-sm tw-text-gray-600">
          Select 3-5 values that resonate with you. Click shuffle to see more options.
        </p>
      </div>

      <div className="tw-flex tw-justify-between tw-items-center">
        <p className="tw-text-xs tw-text-gray-600">Selected: {selectedValues.length} / 5</p>
        <button
          onClick={regenerateDisplayedValues}
          className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-lg tw-border-2 tw-border-gray-200 tw-bg-white tw-text-gray-800 tw-font-medium tw-transition-all hover:tw-border-gray-300"
        >
          <Shuffle size={14} />
          Shuffle Values
        </button>
      </div>

      <div className="tw-grid tw-grid-cols-3 tw-gap-2">
        {displayedValues.map(abbr => {
          const isSelected = selectedValues.includes(abbr);
          const description = getValueDescription(abbr);

          return (
            <button
              key={abbr}
              onClick={() => onToggleValue(abbr)}
              disabled={!isSelected && selectedValues.length >= 5}
              className={`tw-p-2 tw-rounded-lg tw-border-2 tw-transition-all tw-text-center ${
                isSelected
                  ? 'tw-border-blue-600 tw-bg-blue-50'
                  : 'tw-border-gray-200 tw-bg-white hover:tw-border-gray-300'
              } ${!isSelected && selectedValues.length >= 5 ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`}
            >
              <span className={`tw-text-xs tw-font-medium ${isSelected ? 'tw-text-blue-600' : 'tw-text-gray-800'}`}>
                {description}
              </span>
            </button>
          );
        })}
      </div>

      <div className="tw-flex tw-justify-end tw-pt-2">
        <button
          type="button"
          onClick={onContinue}
          disabled={selectedValues.length < 3}
          className="tw-rounded-full tw-px-6 tw-py-2 tw-text-sm tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        >
          Continue to Interests
        </button>
      </div>
    </div>
  );
}
